import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Truck, Shield, Users, ArrowRight, KeyRound, User } from "lucide-react";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Background images for the slideshow - logistics themed
const backgroundImages = [
  "https://images.unsplash.com/photo-1611356013133-c9473fe5c7c3?w=1920&q=80", // Truck at night
  "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1920&q=80", // Warehouse logistics
  "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=1920&q=80", // Container trucks
  "https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?w=1920&q=80", // Highway trucks
  "https://images.unsplash.com/photo-1519003722824-194d4455a60c?w=1920&q=80", // Cargo shipping
  "https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=1920&q=80", // Delivery truck
];

const LoginPage = () => {
  const [mode, setMode] = useState("login"); // login, forgot, reset
  const [userCode, setUserCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentBgIndex, setCurrentBgIndex] = useState(0);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Auto-change background every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBgIndex((prevIndex) => (prevIndex + 1) % backgroundImages.length);
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(userCode, password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid User Code or Password");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email });
      toast.success("If your email is registered, an OTP has been sent to your inbox");
      setMode("reset");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reset code");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API_URL}/auth/reset-password`, {
        email,
        reset_code: resetCode,
        new_password: newPassword
      });
      toast.success("Password reset successfully! You can now login.");
      setMode("login");
      setPassword("");
      setResetCode("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const renderForm = () => {
    if (mode === "forgot") {
      return (
        <form onSubmit={handleForgotPassword} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your registered email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="bg-secondary/50 border-border"
              data-testid="forgot-email-input"
            />
          </div>

          <Button 
            type="submit" 
            className="w-full glow-primary bg-primary hover:bg-primary/90"
            disabled={loading}
            data-testid="forgot-submit-btn"
          >
            {loading ? "Sending..." : "Send Reset Code"}
            <KeyRound className="w-4 h-4 ml-2" />
          </Button>
        </form>
      );
    }

    if (mode === "reset") {
      return (
        <form onSubmit={handleResetPassword} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reset_code">OTP Code</Label>
            <Input
              id="reset_code"
              type="text"
              placeholder="Enter 6-digit OTP from email"
              value={resetCode}
              onChange={(e) => setResetCode(e.target.value)}
              required
              maxLength={6}
              className="bg-secondary/50 border-border font-mono text-center text-xl tracking-widest"
              data-testid="reset-code-input"
            />
            <p className="text-xs text-muted-foreground text-center">Check your email inbox for the OTP</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="new_password">New Password</Label>
            <Input
              id="new_password"
              type="password"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              className="bg-secondary/50 border-border"
              data-testid="reset-new-password-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm_password">Confirm Password</Label>
            <Input
              id="confirm_password"
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="bg-secondary/50 border-border"
              data-testid="reset-confirm-password-input"
            />
          </div>

          <Button 
            type="submit" 
            className="w-full glow-primary bg-primary hover:bg-primary/90"
            disabled={loading}
            data-testid="reset-submit-btn"
          >
            {loading ? "Resetting..." : "Reset Password"}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </form>
      );
    }

    // Login form with User Code or Email
    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="userCode">Login Code / Email</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              id="userCode"
              type="text"
              placeholder="ARD0001 or email@example.com"
              value={userCode}
              onChange={(e) => setUserCode(e.target.value)}
              required
              className="bg-secondary/50 border-border pl-10"
              data-testid="login-usercode-input"
            />
          </div>
          <p className="text-xs text-muted-foreground">Enter your Login Code (e.g., ARD0001) or Email address</p>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="bg-secondary/50 border-border"
            data-testid="login-password-input"
          />
        </div>

        <div className="text-right">
          <button
            type="button"
            onClick={() => setMode("forgot")}
            className="text-primary hover:text-primary/80 text-sm"
            data-testid="forgot-password-link"
          >
            Forgot Password?
          </button>
        </div>

        <Button 
          type="submit" 
          className="w-full glow-primary bg-primary hover:bg-primary/90"
          disabled={loading}
          data-testid="login-submit-btn"
        >
          {loading ? "Signing In..." : "Sign In"}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>

        <div className="mt-4 p-3 bg-secondary/30 rounded-lg border border-border">
          <p className="text-xs text-muted-foreground text-center">
            Default password for all users: <span className="font-mono font-bold text-primary">000000</span>
          </p>
        </div>
      </form>
    );
  };

  const getTitle = () => {
    switch (mode) {
      case "forgot": return "Forgot Password";
      case "reset": return "Reset Password";
      default: return "Welcome Back";
    }
  };

  const getDescription = () => {
    switch (mode) {
      case "forgot": return "Enter your email to receive a password reset OTP";
      case "reset": return "Enter the OTP sent to your email and set a new password";
      default: return "Sign in with your Login Code or Email to access your logistics command center";
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-4 mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_ardhana-log/artifacts/ucgxtluv_WhatsApp%20Image%202026-01-17%20at%2023.49.17.jpeg" 
              alt="Ardhanageshwar Logistics" 
              className="w-16 h-16 rounded-lg"
              data-testid="company-logo"
            />
            <div>
              <h1 className="font-heading text-2xl font-bold text-foreground">ARDHANAGESHWAR</h1>
              <p className="text-muted-foreground text-sm">Logistics Pvt. Ltd.</p>
            </div>
          </div>

          <Card className="border-border bg-card">
            <CardHeader>
              <CardTitle className="font-heading text-2xl">{getTitle()}</CardTitle>
              <CardDescription>{getDescription()}</CardDescription>
            </CardHeader>
            <CardContent>
              {renderForm()}

              <div className="mt-6 text-center space-y-2">
                {(mode === "forgot" || mode === "reset") && (
                  <button
                    type="button"
                    onClick={() => setMode("login")}
                    className="text-primary hover:text-primary/80 text-sm block w-full"
                    data-testid="back-to-login"
                  >
                    Back to Sign In
                  </button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right Side - Hero Image with Auto-changing Background */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Background images with crossfade transition */}
        {backgroundImages.map((img, index) => (
          <div
            key={index}
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000"
            style={{
              backgroundImage: `url('${img}')`,
              opacity: index === currentBgIndex ? 1 : 0,
            }}
          />
        ))}
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-br from-background/90 via-background/50 to-transparent" />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-end p-12">
          <div className="space-y-6">
            <div className="flex items-center gap-4 text-foreground/80">
              <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                <Truck className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="font-heading text-lg font-semibold">Fleet Management</p>
                <p className="text-sm text-muted-foreground">Track and manage your entire fleet</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 text-foreground/80">
              <div className="w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-accent" />
              </div>
              <div>
                <p className="font-heading text-lg font-semibold">HR Management</p>
                <p className="text-sm text-muted-foreground">Manage employees and drivers efficiently</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 text-foreground/80">
              <div className="w-12 h-12 rounded-lg bg-chart-3/20 flex items-center justify-center">
                <Shield className="w-6 h-6 text-chart-3" />
              </div>
              <div>
                <p className="font-heading text-lg font-semibold">Secure & Reliable</p>
                <p className="text-sm text-muted-foreground">Enterprise-grade security for your data</p>
              </div>
            </div>
          </div>
          
          <div className="mt-12 p-6 glass rounded-xl border border-border/50">
            <p className="text-foreground italic">"Streamlining logistics operations across India with cutting-edge technology and reliable service."</p>
            <p className="text-muted-foreground text-sm mt-2">— Ardhanageshwar Logistics Team</p>
          </div>
          
          {/* Image indicator dots */}
          <div className="flex justify-center gap-2 mt-6">
            {backgroundImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentBgIndex(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentBgIndex 
                    ? 'bg-primary w-6' 
                    : 'bg-muted-foreground/50 hover:bg-muted-foreground'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
